import React, { useEffect, useRef, useState } from "react";
import { PRESETS, DATE_BASES, DEFAULT_DATE_BASIS } from "../hooks/usePeriod";
import { formatDate } from "../metrics";

export default function PeriodPicker({ period }) {
  const { preset, range, compare, dateBasis, isWeek, canShiftWeekForward, shiftWeek, setPreset, setCustomRange, setCompare, setDateBasis } =
    period;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: range.from, to: range.to });
  const ref = useRef(null);

  useEffect(() => setDraft({ from: range.from, to: range.to }), [range.from, range.to]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const presetDef = PRESETS.find((p) => p.id === preset);
  const presetLabel = presetDef ? presetDef.label : isWeek ? "Week" : "Custom";

  return (
    <div className={`bd-period ${isWeek ? "bd-period--week" : ""}`} ref={ref}>
      {isWeek && (
        <button type="button" className="bd-period__step" onClick={() => shiftWeek(-1)} aria-label="Previous week" title="Previous week">
          ‹
        </button>
      )}
      <button type="button" className={`bd-period__trigger ${open ? "is-open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <span className="bd-period__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </span>
        <span className="bd-period__label">{isWeek && !presetDef ? "Week of" : presetLabel}</span>
        <span className="bd-period__range">
          {formatDate(range.from, { year: false })} – {formatDate(range.to)}
        </span>
        {dateBasis !== DEFAULT_DATE_BASIS && (
          <span className="bd-period__basis" title={(DATE_BASES.find((d) => d.id === dateBasis) || {}).hint}>
            {(DATE_BASES.find((d) => d.id === dateBasis) || {}).short}
          </span>
        )}
        <span className="bd-period__chev" aria-hidden="true">
          ▾
        </span>
      </button>
      {isWeek && (
        <button
          type="button"
          className="bd-period__step"
          onClick={() => shiftWeek(1)}
          disabled={!canShiftWeekForward}
          aria-label="Next week"
          title={canShiftWeekForward ? "Next week" : "Already on the current week"}
        >
          ›
        </button>
      )}
      {open && (
        <div className="bd-period__menu" role="dialog">
          <div className="bd-period__presets">
            {PRESETS.filter((p) => p.id !== "custom").map((p) => (
              <button
                key={p.id}
                type="button"
                className={`bd-period__preset ${preset === p.id ? "is-active" : ""}`}
                onClick={() => {
                  setPreset(p.id);
                  setOpen(false);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="bd-period__custom">
            <div className="bd-period__custom-title">Custom range</div>
            <label>
              <span>From</span>
              <input type="date" value={draft.from} max={draft.to} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={draft.to} min={draft.from} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </label>
            <button
              type="button"
              className="bd-btn bd-btn--primary"
              disabled={!draft.from || !draft.to}
              onClick={() => {
                setCustomRange(draft.from, draft.to);
                setOpen(false);
              }}
            >
              Apply
            </button>
          </div>
          <div className="bd-period__basis-row">
            <div className="bd-period__basis-label">
              <span>Count points by</span>
              <small>{(DATE_BASES.find((d) => d.id === dateBasis) || DATE_BASES[0]).hint}</small>
            </div>
            <div className="bd-period__segmented" role="radiogroup" aria-label="Count points by">
              {DATE_BASES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={dateBasis === d.id}
                  className={`bd-period__segment ${dateBasis === d.id ? "is-active" : ""}`}
                  title={d.hint}
                  onClick={() => setDateBasis(d.id)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <label className="bd-period__compare">
            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
            <span>Compare with previous period</span>
          </label>
        </div>
      )}
    </div>
  );
}
